import { Injectable, OnDestroy } from '@angular/core';
import { CONSTANTS } from '../../../Constants/Constants';
import { IReportData } from '../../../models/reports/IReportData';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { TranslateService } from '@ngx-translate/core';
import { ITableData } from '../../../models/reports/ITableData';
import { IndexDBService } from '../../common/indexDB.service';
import { Material, MaterialData, MaterialLevelwiseData, SQWebArea, SQWebSystem } from '../../../models/dtos';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { IDataItem } from '../../../models/reports/IDataItem';
import { IData } from '../../../models/reports/IData';
import { ReportTextAlignment } from '../../../models/reports/ReportTextAlignment';
import { ReportsAPIService } from '../../api/reports-api.service';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { Subject } from 'rxjs/internal/Subject';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { CommonDataService } from '../../common-data.service';
import { WiringTypes } from '../../../models/reports/WiringTypes';

@Injectable()
export class WiringTypesService implements OnDestroy {

  public wiringTypeList: string[] = [];
  public wiringTypeNamesList: WiringTypes[] = [];
  public readonly BASEPATH: string = 'assets/WiringImages/';
  public readonly FIRST_COLUMN_WIDTH = '80';   

  private readonly destroy$ = new Subject<void>();
  private cachedTableData: ITableData[] | null = null;

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly translate: TranslateService,
    private readonly indexDataService: IndexDBService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly reportsApiService: ReportsAPIService
  ) { }

  generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true) {
    this.getWiringTypeData(pageOrientation, internalReport);
  }

  private async processReportGeneration(pageOrientation: ReportPageOrientation, internalReport: boolean) {
    // Use cached data if available to avoid re-processing
    if (!this.cachedTableData) {
      let tableData: ITableData[] = [];
      this.wiringTypeList = [];
      await this.generateTableData(tableData);
      this.cachedTableData = tableData;
    }
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportData: IReportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: this.cachedTableData,
      pageOrientation: pageOrientation,
      internalReport: internalReport
    };
    this.reportsCommonService.setSelectedReport(reportData);
  }

  private getWiringTypeData(pageOrientation: ReportPageOrientation, internalReport: boolean) {
    this.wiringTypeNamesList = [];
    this.reportsApiService.getWiringTypeData().pipe(takeUntil(this.destroy$)).subscribe({
      next: (wiringTypes) => {
        this.wiringTypeNamesList = wiringTypes;
        this.processReportGeneration(pageOrientation, internalReport);
      },
      error: (error) => {
        console.error('Error fetching wiring type data:', error);
      }
    });
  }

  async generateTableData(tableData: ITableData[]) {
    const sqWebProject = this.commonDataService.getSQWebProject(); // TODO: Use sqWebProject from commonDataService
    if (sqWebProject.isItemVisible) {
      const materialDataCollection = await this.indexDataService.getAllMaterialData();
      for (const area of sqWebProject.areas) {
        if (area.isItemVisible && area.children && area.children.length > 0) {
          this.processArea(area, materialDataCollection);
        }
      }
    }
    await this.processWiringTypesList(tableData);
  }

  private processArea(area: SQWebArea, materialDataCollection: MaterialLevelwiseData) {
    for (const child of area.children) {
      const childItem = child as SQWebAreaChildren;
      if (childItem.nodeType === CONSTANTS.NODETYPE_AREA) {
        const childArea = childItem as SQWebArea;
        if (childArea.isItemVisible) {
          this.processArea(childArea, materialDataCollection);
        }
      } else if (childItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
        const system = childItem as SQWebSystem;
        if (system.isItemVisible && system.itemUniqueId) {
          let systemdData = materialDataCollection.materialsCollection[system.itemUniqueId];
          if (systemdData && ((system.hasTypicalOf && system.isMaster) || !system.hasTypicalOf)) {
            this.processSystemChildren(systemdData);
          }
        }
      }
    }
  }

  private processSystemChildren(systemChildren: MaterialData) {
    if (!systemChildren.systemChildrenCollection) {
      return;
    }
    for (const item of systemChildren.systemChildrenCollection) {
      const material = item as Material;
      const wiringType = material?.wiringType?.trim();
      if (!wiringType || this.wiringTypeList.includes(wiringType)) {
        continue;
      }
      this.wiringTypeList.push(wiringType);
    }
  }

  async processWiringTypesList(tableData: ITableData[]) {
    this.wiringTypeList.sort((a, b) => a.localeCompare(b));
    for (const wiringType of this.wiringTypeList) {
      const wiringTypeNameObj = this.wiringTypeNamesList.find(wt => wt.wiringTypeID === wiringType);
      const wiringTypeDescription = wiringTypeNameObj ? wiringTypeNameObj.wiringTypeDescription : '';
      const wiringImageName = wiringTypeNameObj ? wiringTypeNameObj.wiringImageName : '';
      const imageBase64String = await this.getImageBase64String(wiringImageName);
      let tableDataItem: ITableData = {
        title: this.getTitleData(wiringType),
        data: this.getDataRow(wiringTypeDescription),
        image: imageBase64String,
      };

      tableData.push(tableDataItem);
    }

     // Add the legend group only when wiring type records exist.
      if (tableData.length > 0) {
        tableData.push(await this.getLeagentGroup());
      }
  }

  getImageBase64String(imageName: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // Return empty string if no image name is provided
      if (!imageName || imageName.trim() === '') {
        resolve('');
        return;
      }
      const imageUrl = this.BASEPATH + imageName;
      const img = new Image();

      // Helper to clean up event handlers after use
      const cleanup = () => {
        img.onload = null;
        img.onerror = null;
      };
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0);
        const base64String = canvas.toDataURL('image/png');
        cleanup();
        resolve(base64String);
      };
      img.onerror = (error) => {
        console.error(`Error loading image: ${imageUrl}`, error);
        cleanup();
        resolve(''); // Resolve with empty string instead of rejecting
      };
      // Set src after handlers are attached
      img.src = imageUrl;
    });
  }

  getTitleData(wiringType: string): IDataItem[] {
    const alignment: ReportTextAlignment = 'left';
    return [
      {
        data: `${this.translate.instant('WIRING_TYPE')}${CONSTANTS.COLON}`,
        columnspan: 1,
        hierarchyLevel: 0,
        DataCellColor: CONSTANTS.REPORT_LIGHT_BLUE_COLOR,
        alignment: alignment,
        width: this.FIRST_COLUMN_WIDTH
      },
      {
        data: wiringType,
        columnspan: 1,
        hierarchyLevel: 0,
        DataCellColor: CONSTANTS.REPORT_ORANGE_COLOR,
        alignment: alignment
      }
    ];
  }

  getDataRow(wiringTypeDescription: string): IData[] {
    const alignment: ReportTextAlignment = 'left';
    return [
      {
        data: [
          {
            data: `${this.translate.instant('DESCRIPTION')}${CONSTANTS.COLON}`,
            columnspan: 1,
            hierarchyLevel: 0,
            width: this.FIRST_COLUMN_WIDTH,
            alignment: alignment
          },
          {
            data: wiringTypeDescription,
            columnspan: 1,
            hierarchyLevel: 0,
            alignment: alignment
          }
        ]
      }
    ];
  }

    private async getLeagentGroup(): Promise<ITableData> {
      // Four columns: [image, text, image, text]
      const title = [...this.getTitleData(''), ...this.getTitleData('')];
      title[0].data = `${this.translate.instant('LEGEND')}${CONSTANTS.COLON}`;
      title[0].width = this.FIRST_COLUMN_WIDTH;
      title[1].columnspan = 3;     
      title[1].width = '*';
      title[2].width = this.FIRST_COLUMN_WIDTH;
      title[3].width = '150';

      const staticItems: { label: string; value: string }[] = [
        {
          label: '1',
          value: 'MAXIMUM WIRE RUN LENGTHS ARE BASED ON THE CURRENT DRAW AND WIRE GAGE SEE DRAWING PWIR.'
        },
        {
          label: '9',
          value: '50mA OR LESS - 750ft/230m,                       50mA TO 100mA - 375ft/115m'
        },
        {
          label: '2',
          value: 'REFER TO SPECIFIC PANEL TERMINATION DRAWINGS FOR DETAILED INFORMATION ON TERMINATIONS.'
        },
        {
          label: '10',
          value: '100mA TO 150mA - 250ft/76m,                       150mA TO 200mA - 187ft/57m,                       200mA TO 250mA - 150ft/46m'
        }
      ];

      const itemCells: IDataItem[][] = await Promise.all(staticItems.map(async item => [
        {
          data: await this.getLegendLabelImage(item.label),
          columnspan: 1,
          hierarchyLevel: 0,
          alignment: 'center' as ReportTextAlignment,
          renderAsImage: true,
          imageWidth: 28,
          imageFit: [28, 24] as [number, number]
        },
        {
          data: item.value,
          columnspan: 1,
          hierarchyLevel: 0,
          alignment: 'left' as ReportTextAlignment
        }
      ]));

      const data: IData[] = [];
      for (let i = 0; i < itemCells.length; i += 2) {
        data.push({ data: [...itemCells[i], ...(itemCells[i + 1] ?? [])] });
      }

    return { title, data };
  } 

  private getLegendLabelImage(label: string): Promise<string> {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="30" viewBox="0 0 40 30">
                    <polygon points="20,1 33,8 33,22 20,29 7,22 7,8" fill="white" stroke="black" stroke-width="1"/>
                    <text x="20" y="21" font-size="16" font-family="Arial" font-weight="bold" fill="red" text-anchor="middle">${label}</text>
                </svg>`;

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 24;
        const context = canvas.getContext('2d');
        if (context) {
          context.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/png'));
        } else {
          resolve('');
        }
      };
      img.onerror = () => resolve('');
      img.src = `data:image/svg+xml;base64,${btoa(svg)}`;
    });
  }
  
  clearCache(): void {
    this.cachedTableData = null;
    this.wiringTypeList = [];
    this.wiringTypeNamesList = [];
  }

  // This method will be called if the service is provided at a component level
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearCache();
  }
}
